"use client";

import * as React from "react";

const VIDEO_SRC =
  "https://vconqnpmybosduyhtbmu.supabase.co/storage/v1/object/public/Videos/bg-general.mp4";
const VIDEO_DURATION = 5; // seconds per loop
const CROSSFADE_DURATION = 2; // fade in / fade out length
const MIDDLE_DURATION = VIDEO_DURATION - CROSSFADE_DURATION * 2; // 1s at full opacity
const STORAGE_KEY = "zc-background-mode"; // shared with the ZC Knowledge Base

function getOpacity(time: number): number {
  if (time < CROSSFADE_DURATION) return time / CROSSFADE_DURATION;
  if (time < CROSSFADE_DURATION + MIDDLE_DURATION) return 1;
  return 1 - (time - CROSSFADE_DURATION - MIDDLE_DURATION) / CROSSFADE_DURATION;
}
const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

/** Motion is opt-out by several independent signals, any one of which wins. */
function shouldPlayMotion(): boolean {
  if (typeof window === "undefined") return false;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "static") return false;
  // Automated browsers (Playwright/Selenium drive the integration suite —
  // upstream's tests exercise nearly every poll page) must not spend CI
  // bandwidth/CPU on two 17 MB ambiance videos per page: dialog exit
  // animations start hanging under that load and clicks get intercepted.
  // An EXPLICIT "motion" preference still wins so visual QA can opt in.
  if (navigator.webdriver && stored !== "motion") return false;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches)
    return false;
  const conn = (
    navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string };
    }
  ).connection;
  if (conn?.saveData) return false;
  if (conn?.effectiveType && /2g/.test(conn.effectiveType)) return false;
  return true;
}

export function ZenVideoBackground() {
  const videoARef = React.useRef<HTMLVideoElement>(null);
  const videoBRef = React.useRef<HTMLVideoElement>(null);
  // Never render <video> on the server: the decision depends on localStorage
  // and matchMedia, and a wrong first paint downloads 17 MB before we can
  // cancel it.
  const [motion, setMotion] = React.useState(false);

  React.useEffect(() => {
    setMotion(shouldPlayMotion());
  }, []);

  React.useEffect(() => {
    if (!motion) return;
    const videoA = videoARef.current;
    const videoB = videoBRef.current;
    if (!videoA || !videoB) return;

    let frameId = 0;
    let cancelled = false;

    const animate = () => {
      if (cancelled) return;
      videoA.style.opacity = String(clamp01(getOpacity(videoA.currentTime)));
      videoB.style.opacity = String(clamp01(getOpacity(videoB.currentTime)));
      frameId = requestAnimationFrame(animate);
    };
    const initialize = () => {
      if (cancelled) return;
      videoA.currentTime = 0;
      videoB.currentTime = VIDEO_DURATION - CROSSFADE_DURATION; // 3s offset
      videoA.play().catch(() => {});
      videoB.play().catch(() => {});
      frameId = requestAnimationFrame(animate);
    };

    let aReady = videoA.readyState >= 3;
    let bReady = videoB.readyState >= 3;
    let started = false;
    const checkReady = () => {
      if (!started && aReady && bReady) {
        started = true;
        initialize();
      }
    };
    const onA = () => {
      aReady = true;
      checkReady();
    };
    const onB = () => {
      bReady = true;
      checkReady();
    };
    videoA.addEventListener("canplaythrough", onA, { once: true });
    videoB.addEventListener("canplaythrough", onB, { once: true });
    checkReady();

    return () => {
      cancelled = true;
      if (frameId) cancelAnimationFrame(frameId);
      videoA.removeEventListener("canplaythrough", onA);
      videoB.removeEventListener("canplaythrough", onB);
      videoA.pause();
      videoB.pause();
    };
  }, [motion]);

  const videoStyle: React.CSSProperties = {
    position: "absolute",
    top: "50%",
    left: "50%",
    minWidth: "100%",
    minHeight: "100%",
    width: "auto",
    height: "auto",
    transform: "translate(-50%, -50%)",
    objectFit: "cover",
    opacity: 0,
    transition: "opacity 0.05s linear",
  };

  return (
    <div
      aria-hidden
      data-zen-video
      style={{
        position: "fixed",
        inset: 0,
        zIndex: -1,
        overflow: "hidden",
        pointerEvents: "none",
        // The static fallback. Always painted; the video (when it plays)
        // covers it. No network dependency, no layout shift, no flash.
        backgroundColor: "#0B0D0C",
        backgroundImage:
          "radial-gradient(120% 80% at 50% 0%, rgba(201,169,98,0.10), transparent 60%)," +
          "radial-gradient(100% 70% at 50% 100%, rgba(93,184,196,0.06), transparent 60%)," +
          "radial-gradient(60% 50% at 50% 45%, rgba(201,169,98,0.05), transparent 70%)",
      }}
    >
      {motion ? (
        <>
          <video
            ref={videoARef}
            muted
            loop
            playsInline
            preload="auto"
            style={videoStyle}
          >
            <source src={VIDEO_SRC} type="video/mp4" />
          </video>
          <video
            ref={videoBRef}
            muted
            loop
            playsInline
            preload="auto"
            style={videoStyle}
          >
            <source src={VIDEO_SRC} type="video/mp4" />
          </video>
        </>
      ) : null}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0, 0, 0, 0.75)",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />
    </div>
  );
}
